import React from 'react';

import { Form } from 'antd';
import styled from 'styled-components';

import { isCommunityCCRC } from 'components/Community/utils';
import Checkbox from 'components/lib/Checkbox';
import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { getAreaUnitLabel } from 'utils/helpers/units';

import { CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE } from '../constants';

const Container = styled.div`
  display: flex;
  flex-flow: column;
  position: relative;
`;

const Title = styled.div`
  letter-spacing: 0.5px;
  color: var(--text-primary);
  font-family: var(--font-bold);
  font-size: 12px;
  margin-bottom: 10px;
`;

const Label = styled.span`
  font-size: 14px;
  margin-left: 10px;
`;

const LabelContainer = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: 10px;

  span {
    display: flex;
  }
`;

const PaddedContainer = styled.div`
  padding-left: 20px;
`;

type CheckboxRowType = {
  formItemName: string | string[];
  label: string;
  style?: React.CSSProperties;
  disabled?: boolean;
};

const CheckboxRow = ({ formItemName, label, style, disabled }: CheckboxRowType) => (
  <LabelContainer style={style}>
    <Form.Item noStyle name={formItemName} valuePropName="checked">
      <Checkbox className={'green'} disabled={disabled} />
    </Form.Item>
    <Label>{label}</Label>
  </LabelContainer>
);

const getCompactAreaUnitLabel = (unitSource?: any) => {
  const [, unit] = getAreaUnitLabel(unitSource).split(' ');
  return `Sq.${unit.charAt(0).toUpperCase()}${unit.slice(1)}`;
};

const CMASummaryViewFilterCategories = () => {
  const { community, competitorId } = useCommunityViewContext();
  const areaUnitLabel = getCompactAreaUnitLabel(community);

  const isCCRC = isCommunityCCRC(community?.rental_types || []);
  const feesToShow = Object.values(CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE).filter(
    (fee) => fee.id !== CMA_SUMMARY_DATA_FEE_INCENTIVE_ROW_TYPE.SECOND_PERSON_ENTRANCE_FEE.id || isCCRC
  );

  return (
    <Container>
      <Title>CATEGORIES</Title>
      <CheckboxRow formItemName={'contactInformation'} label="Contact Information" />
      <PaddedContainer>
        <CheckboxRow formItemName={'address'} label="Address" />
        <CheckboxRow formItemName={'distance'} label="Distance" />
        <CheckboxRow formItemName={'website'} label="Website" />
        <CheckboxRow formItemName={'yearOpened'} label="Year Opened" />
        <CheckboxRow formItemName={'phone'} label="Phone" />
        <CheckboxRow formItemName={'communityNotes'} label="Community Notes" />
      </PaddedContainer>
      <CheckboxRow formItemName={'reviews'} label="Reviews" />
      <CheckboxRow formItemName={'shopDate'} label="Shop Date" />
      <CheckboxRow formItemName={'careTypes'} label="Care Types" />
      <CheckboxRow formItemName={'occupancy'} label="Occupancy (overall)" />
      <CheckboxRow formItemName={'ratesFees'} label="Rates & Fees" />
      <PaddedContainer>
        {isCommunityCCRC(community?.rental_types || []) && (
          <CheckboxRow formItemName={'contractBenefits'} label="Contract Benefits" />
        )}
        <CheckboxRow formItemName={'roomType'} label="Room Type" />
        <PaddedContainer>
          <CheckboxRow formItemName={'squareFootage'} label={areaUnitLabel} />
          <CheckboxRow formItemName={'pricePerSquareFootage'} label={`Rate per ${areaUnitLabel}`} />
        </PaddedContainer>
        {feesToShow.map((fee) => (
          <CheckboxRow key={fee.id} formItemName={fee.field} label={fee.label} />
        ))}
        <CheckboxRow formItemName={'careFee'} label="Care Fee" />
        <PaddedContainer>
          <CheckboxRow formItemName={'feeNote'} label="Fee Notes" />
        </PaddedContainer>
        <CheckboxRow formItemName={'medicationFee'} label="Medication Fee" />
        <PaddedContainer>
          <CheckboxRow formItemName={'medicationFeeNote'} label="Fee Notes" />
        </PaddedContainer>
      </PaddedContainer>
      <CheckboxRow formItemName={'incentives'} label="Incentives" style={{ marginBottom: 0 }} />
      <CheckboxRow formItemName={'averageCompetitors'} label="Average (Competitors)" disabled={Boolean(competitorId)} />
    </Container>
  );
};

export default CMASummaryViewFilterCategories;
